/**
 * PLZ side teaser → Klaviyo signup form (10% off).
 *
 * Klaviyo constraints on this store (form RUqc7k):
 *  - Close leaves a dead shell (opacity 0 + pointer-events) that blocks the page.
 *  - openForm() cannot remount after a real close in the same page load.
 *
 * Strategy:
 *  - Clean the dead shell only AFTER a confirmed close (never during fade-in).
 *  - Reopen after a real close via a page reload + session flag.
 *  - Early teaser clicks (before auto-popup / before Klaviyo is ready) keep
 *    retrying openForm — they must NOT flip into "reload" mode until the
 *    shopper has actually seen and closed the modal.
 */
(function () {
  'use strict';

  if (window.__plzNlTeaserBooted) return;
  window.__plzNlTeaserBooted = true;

  var HIDE_KEY = 'plzNlTeaserHidden';
  var REOPEN_KEY = 'plzNlTeaserReopen';
  var DEFAULT_FORM_ID = 'RUqc7k';
  var LEGACY_FORM_IDS = ['WM7TYK', 'R7wpdP'];
  var OPEN_GRACE_MS = 10000;
  var DEAD_SHELL_MS = 1500;

  var openTimers = [];
  var closeTimers = [];
  var formIsOpen = false;
  var sawLiveModal = false;
  var formWasClosed = false;
  var openingInProgress = false;
  var openGraceUntil = 0;
  var awaitingCleanup = false;
  var deadSince = 0;

  function getRoot() {
    return document.querySelector('[data-plz-nl-teaser]');
  }

  function getFormId(root) {
    return ((root && root.getAttribute('data-klaviyo-form-id')) || DEFAULT_FORM_ID).trim();
  }

  function shouldHideAfterSubmit(root) {
    return root && root.getAttribute('data-hide-after-submit') === 'true';
  }

  function readSession(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeSession(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      /* ignore */
    }
  }

  function clearSession(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      /* ignore */
    }
  }

  function showTeaser(root) {
    if (!root) return;
    root.hidden = false;
    root.removeAttribute('hidden');
  }

  function hideTeaser(root) {
    if (!root) return;
    root.hidden = true;
    root.setAttribute('hidden', '');
  }

  function clearOpenTimers() {
    openTimers.forEach(function (id) {
      window.clearTimeout(id);
    });
    openTimers = [];
  }

  function clearCloseTimers() {
    closeTimers.forEach(function (id) {
      window.clearTimeout(id);
    });
    closeTimers = [];
  }

  function isKlaviyoNode(el) {
    if (!el || el.nodeType !== 1) return false;
    var cls = typeof el.className === 'string' ? el.className : '';
    return cls.indexOf('kl-private') !== -1 || cls.indexOf('klaviyo') !== -1 || cls.indexOf('needsclick') !== -1;
  }

  function getModalShells() {
    var shells = [];

    Array.prototype.forEach.call(document.querySelectorAll('[role="dialog"]'), function (el) {
      if (isKlaviyoNode(el) && shells.indexOf(el) === -1) shells.push(el);
    });

    Array.prototype.forEach.call(document.body.children, function (child) {
      if (child.closest('[data-plz-nl-teaser], [data-plz-checkout-email-gate]')) return;
      if (child.querySelector && child.querySelector('button.klaviyo-close-form')) {
        if (shells.indexOf(child) === -1) shells.push(child);
      }
    });

    return shells;
  }

  function isModalLive() {
    var shells = getModalShells();
    for (var i = 0; i < shells.length; i++) {
      try {
        var style = window.getComputedStyle(shells[i]);
        var rect = shells[i].getBoundingClientRect();
        if (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          parseFloat(style.opacity) > 0.5 &&
          rect.width > 100 &&
          rect.height > 100
        ) {
          return true;
        }
      } catch (error) {}
    }
    return false;
  }

  function releaseBodyScroll() {
    try {
      var body = document.body;
      body.classList.remove('klaviyo-prevent-body-scrolling');
      body.classList.remove('overflow-hidden');
      body.style.removeProperty('overflow');
      body.style.removeProperty('pointer-events');
      body.style.removeProperty('touch-action');
      body.style.removeProperty('position');
      body.style.removeProperty('top');
      body.style.removeProperty('width');
      document.documentElement.style.removeProperty('overflow');
    } catch (error) {}
  }

  function clearFormSuppression(formId) {
    if (!formId) return;
    try {
      var raw = localStorage.getItem('klaviyoOnsite');
      if (!raw) return;
      var data = JSON.parse(raw);

      function scrub(obj) {
        if (!obj || typeof obj !== 'object') return;
        Object.keys(obj).forEach(function (key) {
          var value = obj[key];
          if (key === formId) {
            delete obj[key];
            return;
          }
          if (value && typeof value === 'object') {
            if (Object.prototype.hasOwnProperty.call(value, formId)) delete value[formId];
            scrub(value);
          }
        });
      }

      scrub(data);
      localStorage.setItem('klaviyoOnsite', JSON.stringify(data));
    } catch (error) {
      /* ignore */
    }
  }

  function markOpening() {
    formIsOpen = true;
    openingInProgress = true;
    awaitingCleanup = false;
    deadSince = 0;
    openGraceUntil = Date.now() + OPEN_GRACE_MS;
  }

  function markLive() {
    sawLiveModal = true;
    formIsOpen = true;
    openingInProgress = false;
    awaitingCleanup = false;
    deadSince = 0;
    openGraceUntil = Date.now() + OPEN_GRACE_MS;
  }

  /** Only call after a confirmed close — never during fade-in. */
  function removeDeadModal() {
    if (Date.now() < openGraceUntil) return false;
    if (isModalLive()) return false;

    var removed = false;
    getModalShells().forEach(function (el) {
      try {
        el.remove();
        removed = true;
      } catch (error) {}
    });

    releaseBodyScroll();
    formIsOpen = false;
    openingInProgress = false;
    awaitingCleanup = false;
    deadSince = 0;
    return removed;
  }

  function scheduleCleanupAfterClose() {
    awaitingCleanup = true;
    openGraceUntil = 0;
    formIsOpen = false;
    openingInProgress = false;
    if (sawLiveModal) formWasClosed = true;
    clearOpenTimers();
    clearCloseTimers();

    closeTimers.push(
      window.setTimeout(removeDeadModal, 450),
      window.setTimeout(removeDeadModal, 1000),
      window.setTimeout(removeDeadModal, 2000)
    );
  }

  function queueOpenForm(formId) {
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', formId]);
    try {
      if (typeof window._klOnsite.openForm === 'function') window._klOnsite.openForm([formId]);
    } catch (error) {}
    try {
      if (window.klaviyo && typeof window.klaviyo.openForm === 'function') window.klaviyo.openForm(formId);
    } catch (error) {}
  }

  function openFormWithRetries(formId) {
    clearOpenTimers();
    clearCloseTimers();
    markOpening();
    clearFormSuppression(formId);
    queueOpenForm(formId);

    // Long window: early clicks often happen before Klaviyo.js is ready.
    [200, 500, 1000, 1600, 2500, 4000, 6000, 9000, 12000].forEach(function (delay) {
      openTimers.push(
        window.setTimeout(function () {
          if (isModalLive()) {
            markLive();
            clearOpenTimers();
            return;
          }
          clearFormSuppression(formId);
          queueOpenForm(formId);
        }, delay)
      );
    });

    // If nothing appeared after retries, allow another normal click (no reload).
    openTimers.push(
      window.setTimeout(function () {
        if (!isModalLive()) {
          openingInProgress = false;
          formIsOpen = false;
        }
      }, 13000)
    );
  }

  /** Klaviyo cannot remount a closed form, so reopen through a page reload. */
  function reopenViaReload(formId) {
    writeSession(REOPEN_KEY, formId);
    releaseBodyScroll();
    window.location.reload();
  }

  function onTeaserClick(event) {
    var root = getRoot();
    if (!root) return;
    var btn = event.target.closest('[data-plz-nl-teaser-open]');
    if (!btn || !root.contains(btn)) return;

    event.preventDefault();
    event.stopPropagation();

    if (isModalLive()) return;

    var formId = getFormId(root);

    // Only reload after the shopper actually saw + closed the popup.
    // Early clicks (before auto-popup) must keep calling openForm.
    if (formWasClosed) {
      reopenViaReload(formId);
      return;
    }

    openFormWithRetries(formId);
  }

  function onKlaviyoFormEvent(event) {
    var detail = event && event.detail;
    if (!detail) return;
    var root = getRoot();
    var formId = getFormId(root);
    var eventFormId = detail.formId || detail.formID || '';
    if (eventFormId && formId && eventFormId !== formId) return;

    var type = String(detail.type || '').toLowerCase();

    if (type === 'open' || type === 'embedopen') {
      clearOpenTimers();
      clearCloseTimers();
      markLive();
      return;
    }

    if (type === 'close' || type === 'closed' || type === 'dismiss' || type === 'dismissed') {
      scheduleCleanupAfterClose();
      return;
    }

    if (type === 'submit' || type === 'redirectedtocl') {
      if (root && shouldHideAfterSubmit(root)) {
        writeSession(HIDE_KEY, '1');
        hideTeaser(root);
      }
      scheduleCleanupAfterClose();
    }
  }

  function hideNativeTeasers() {
    var nodes = document.querySelectorAll(
      'button.needsclick[aria-label="Open Form"], [class*="kl-teaser-"], [role="button"][class*="kl-teaser-"]'
    );

    Array.prototype.forEach.call(nodes, function (node) {
      if (node.closest('[data-plz-nl-teaser]')) return;
      try {
        var rect = node.getBoundingClientRect();
        if (rect.width >= 420 && rect.height >= 420) return;
      } catch (error) {}

      node.style.setProperty('display', 'none', 'important');
      node.style.setProperty('pointer-events', 'none', 'important');
      node.setAttribute('aria-hidden', 'true');
    });
  }

  function watchdog() {
    hideNativeTeasers();

    if (Date.now() < openGraceUntil) {
      if (isModalLive()) markLive();
      return;
    }

    if (isModalLive()) {
      markLive();
      return;
    }

    var shells = getModalShells();
    var prevent = document.body.classList.contains('klaviyo-prevent-body-scrolling');

    if (!shells.length) {
      deadSince = 0;
      if (prevent) releaseBodyScroll();
      formIsOpen = false;
      return;
    }

    if (!deadSince) deadSince = Date.now();

    if (awaitingCleanup || Date.now() - deadSince >= DEAD_SHELL_MS) {
      removeDeadModal();
    }
  }

  function init() {
    var root = getRoot();
    if (!root) return;

    if (LEGACY_FORM_IDS.indexOf(root.getAttribute('data-klaviyo-form-id')) !== -1) {
      root.setAttribute('data-klaviyo-form-id', DEFAULT_FORM_ID);
    }

    if (readSession(HIDE_KEY) === '1') {
      hideTeaser(root);
      return;
    }

    showTeaser(root);
    document.addEventListener('click', onTeaserClick, true);
    window.addEventListener('klaviyoForms', onKlaviyoFormEvent);
    hideNativeTeasers();
    window.setInterval(watchdog, 700);

    var pending = readSession(REOPEN_KEY);
    if (pending) {
      clearSession(REOPEN_KEY);
      openFormWithRetries(pending);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
